// Enhanced validation utilities with stricter email and phone validation

// List of common disposable email domains
const DISPOSABLE_EMAIL_DOMAINS = [
  'tempmail.com', 'throwaway.email', '10minutemail.com', 'guerrillamail.com',
  'mailinator.com', 'yopmail.com', 'trashmail.com', 'getnada.com',
  'maildrop.cc', 'dispostable.com', 'tempr.email', 'throwawaymail.com',
  'fakemail.net', 'mintemail.com', 'sharklasers.com', 'guerrillamail.info',
  'spam4.me', 'grr.la', 'mailnesia.com', 'temporary-mail.net'
];

// List of test/example domains
const TEST_EMAIL_DOMAINS = [
  'test.com', 'example.com', 'example.org', 'example.net',
  'test.org', 'test.net', 'testing.com', 'demo.com'
];

// Common fake phone patterns
const FAKE_PHONE_PATTERNS = [
  /^(\d)\1{9}$/,        // 1111111111, 2222222222, etc.
  /^1234567890$/,       // 1234567890
  /^9876543210$/,       // 9876543210
  /^0{10}$/             // 0000000000
];

export const enhancedValidators = {
  ...require('./validation').validators,

  // Enhanced email validation
  email: (value) => {
    if (!value) return null;
    
    // Basic format check
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    
    if (!emailRegex.test(value)) {
      return 'Please enter a valid email address';
    }

    const [localPart, domain] = value.toLowerCase().split('@');
    
    // Check for disposable email domains
    if (DISPOSABLE_EMAIL_DOMAINS.includes(domain)) {
      return 'Disposable email addresses are not allowed';
    }
    
    // Check for test/example domains - but allow our placeholder domain
    if (TEST_EMAIL_DOMAINS.includes(domain) && !domain.includes('radix.trading')) {
      return 'Please use a real email address';
    }
    
    // Check for obvious fake patterns
    if (localPart === 'test' || localPart === 'fake' || localPart === 'example') {
      return 'Please use a real email address';
    }
    
    // Check minimum length for local part
    if (localPart.length < 2) {
      return 'Email address appears to be invalid';
    }
    
    // Check for repeated characters
    if (/^(.)\1+$/.test(localPart)) {
      return 'Please enter a valid email address';
    }
    
    return null;
  },

  // Enhanced phone validation
  phone: (value) => {
    if (!value) return null;
    
    const digitsOnly = value.replace(/\D/g, '');
    
    // Check length
    if (digitsOnly.length !== 10) {
      return 'Phone number must be exactly 10 digits';
    }
    
    // Check for fake patterns
    for (const pattern of FAKE_PHONE_PATTERNS) {
      if (pattern.test(digitsOnly)) {
        return 'Please enter a real phone number';
      }
    }
    
    // Check for valid US phone number format
    // First digit of area code cannot be 0 or 1
    if (digitsOnly[0] === '0' || digitsOnly[0] === '1') {
      return 'Invalid area code';
    }
    
    // Note: We're removing the check for second digit being 9 and exchange starting with 0/1
    // as these are actually valid in many US phone numbers
    
    return null;
  },

  // Enhanced address validation
  address: (value) => {
    if (!value) return null;
    
    const trimmed = value.trim();
    
    // Check minimum length
    if (trimmed.length < 10) {
      return 'Please enter a complete street address';
    }
    
    // Check for obvious fake addresses
    const fakePatternsAddress = [
      /^test/i,
      /^fake/i,
      /^123 test/i,
      /^asdf/i,
      /^qwerty/i,
      /^1{3,}/,
      /^abc/i,
      /^xxx/i
    ];
    
    for (const pattern of fakePatternsAddress) {
      if (pattern.test(trimmed)) {
        return 'Please enter a real street address';
      }
    }
    
    // Check for street number
    if (!/^\d+/.test(trimmed)) {
      return 'Street address should start with a number';
    }
    
    // Check for street name (at least 2 words)
    const words = trimmed.split(/\s+/);
    if (words.length < 2) {
      return 'Please enter a complete street address';
    }
    
    // Check for common street suffixes
    const lastWord = words[words.length - 1].toLowerCase();
    const validSuffixes = [
      'street', 'st', 'avenue', 'ave', 'road', 'rd', 'drive', 'dr',
      'lane', 'ln', 'boulevard', 'blvd', 'court', 'ct', 'place', 'pl',
      'way', 'circle', 'cir', 'park', 'parkway', 'pkwy', 'square', 'sq',
      'trail', 'tr', 'highway', 'hwy'
    ];
    
    // Allow apartment/unit numbers
    const hasAptUnit = /\b(apt|apartment|unit|suite|ste|#)\b/i.test(trimmed);
    
    if (!validSuffixes.includes(lastWord) && !hasAptUnit) {
      // Check if second to last word is a valid suffix (for "123 Main Street Apt 5" format)
      if (words.length >= 3) {
        const secondToLast = words[words.length - 2].toLowerCase();
        if (!validSuffixes.includes(secondToLast)) {
          return 'Please enter a valid street address';
        }
      } else {
        return 'Please enter a valid street address';
      }
    }
    
    return null;
  },

  // City validation
  city: (value) => {
    if (!value) return null;
    
    const trimmed = value.trim();
    
    // Check minimum length
    if (trimmed.length < 2) {
      return 'Please enter a valid city name';
    }
    
    // Check for numbers (cities shouldn't have numbers)
    if (/\d/.test(trimmed)) {
      return 'City name should not contain numbers';
    }
    
    // Check for fake patterns
    const fakeCityPatterns = [
      /^test/i,
      /^fake/i,
      /^city/i,
      /^town/i,
      /^asdf/i,
      /^xxx/i,
      /^aaa/i
    ];
    
    for (const pattern of fakeCityPatterns) {
      if (pattern.test(trimmed)) {
        return 'Please enter a real city name';
      }
    }
    
    // Check for repeated characters
    if (/^(.)\1+$/.test(trimmed)) {
      return 'Please enter a valid city name';
    }
    
    return null;
  },

  // State validation with list of valid states
  state: (value) => {
    if (!value) return null;
    
    const validStates = [
      'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
      'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
      'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
      'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
      'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
      'DC' // Washington D.C.
    ];
    
    const upperValue = value.toUpperCase();
    
    if (!validStates.includes(upperValue)) {
      return 'Please enter a valid US state code';
    }
    
    return null;
  },

  // ZIP code validation with basic geographic checks
  zipCode: (value) => {
    if (!value) return null;
    
    const zipRegex = /^\d{5}(-\d{4})?$/;
    if (!zipRegex.test(value)) {
      return 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
    }
    
    const mainZip = value.substring(0, 5);
    
    // Check for invalid ZIP codes (00000, 11111, etc.)
    if (/^(\d)\1{4}$/.test(mainZip)) {
      return 'Please enter a valid ZIP code';
    }
    
    // ZIP codes starting with 00 are for Puerto Rico and Virgin Islands
    // ZIP codes starting with 09 are for Armed Forces
    // All others should be between 01000 and 99999
    const zipNum = parseInt(mainZip);
    if (zipNum === 0) {
      return 'Please enter a valid ZIP code';
    }
    
    return null;
  }
};

// Async validation functions for server-side checks
export const asyncValidators = {
  // Could be used to verify email deliverability via API
  emailDeliverable: async (email) => {
    // Placeholder for email verification service
    // In production, you might use a service like:
    // - Kickbox
    // - NeverBounce
    // - Email List Verify
    // - Hunter.io
    
    // For now, just return the sync validation
    return enhancedValidators.email(email);
  },

  // Could be used to verify phone number via API
  phoneVerifiable: async (phone) => {
    // Placeholder for phone verification service
    // In production, you might use a service like:
    // - Twilio Lookup API
    // - NumVerify
    // - Nexmo Number Insight
    
    // For now, just return the sync validation
    return enhancedValidators.phone(phone);
  },

  // Could be used to verify address via API
  addressVerifiable: async (addressData) => {
    // Placeholder for address verification service
    // In production, you might use a service like:
    // - USPS Address Verification
    // - Google Address Validation API
    // - SmartyStreets
    // - Melissa Address Verification
    
    // For now, combine all address field validations
    const errors = {};
    
    if (addressData.address) {
      const addressError = enhancedValidators.address(addressData.address);
      if (addressError) errors.address = addressError;
    }
    
    if (addressData.city) {
      const cityError = enhancedValidators.city(addressData.city);
      if (cityError) errors.city = cityError;
    }
    
    if (addressData.state) {
      const stateError = enhancedValidators.state(addressData.state);
      if (stateError) errors.state = stateError;
    }
    
    if (addressData.zip) {
      const zipError = enhancedValidators.zipCode(addressData.zip);
      if (zipError) errors.zip = zipError;
    }
    
    return Object.keys(errors).length > 0 ? errors : null;
  }
};

// Export the same utility functions from the original validation
export { formatPhoneNumber, formatZipCode } from './validation';